import React, { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle, Loader2, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import withdrawalService from '../../services/withdrawalService';

const WithdrawalModal = ({ project, onClose, onSuccess }) => {
  // Multi-currency state
  const [allBalances, setAllBalances] = useState({});
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);

  const [formData, setFormData] = useState({
    amount: '',
    recipientWallet: '',
    bankCode: '',
    bankName: '',
    accountNumber: '',
    accountName: '',
    reason: ''
  });

  const [banks, setBanks] = useState([]);
  const [isResolvingAccount, setIsResolvingAccount] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingBalance, setIsLoadingBalance] = useState(true);
  const [fees, setFees] = useState({ baseFee: 0, vat: 0, totalFee: 0 });

  // Derived state
  const isNGN = selectedCurrency === 'NGN';
  const currentBalance = allBalances[selectedCurrency]?.available || 0;
  const balanceBreakdown = allBalances[selectedCurrency] || null;

  // Load all balances on mount
  useEffect(() => {
    loadAllBalances();
  }, []);

  // Load banks when NGN is selected
  useEffect(() => {
    if (isNGN && banks.length === 0) {
      fetchBanks();
    }
  }, [selectedCurrency]);

  // Resolve account name when account number is 10 digits
  useEffect(() => {
    if (isNGN && formData.accountNumber.length === 10 && formData.bankCode) {
      resolveAccountName();
    }
  }, [formData.accountNumber, formData.bankCode]);

  // Handle fee calculation for NGN
  useEffect(() => {
    if (isNGN && formData.amount) {
      const amount = parseFloat(formData.amount);
      if (!isNaN(amount) && amount > 0) {
        let baseFee = 0;
        if (amount <= 5000) baseFee = 10;
        else if (amount <= 50000) baseFee = 25;
        else baseFee = 50;
        const vat = baseFee * 0.075;
        setFees({ baseFee, vat, totalFee: baseFee + vat });
      } else {
        setFees({ baseFee: 0, vat: 0, totalFee: 0 });
      }
    } else {
      setFees({ baseFee: 0, vat: 0, totalFee: 0 });
    }
  }, [formData.amount, isNGN]);

  const loadAllBalances = async () => {
    try {
      setIsLoadingBalance(true);
      const response = await withdrawalService.getAllBalances(project.id);
      //console.log('💰 All Balances Response:', response);

      if (response.success && response.data?.balances) {
        const balances = response.data.balances;
        setAllBalances(balances);

        // Determine available currencies (those with available > 0)
        const currencies = Object.keys(balances).filter(
          curr => balances[curr]?.available > 0 || balances[curr]?.gross > 0
        );
        setAvailableCurrencies(currencies);

        // Default to project currency or first available
        const defaultCurrency = currencies.includes(project.currency?.toUpperCase())
          ? project.currency.toUpperCase()
          : currencies[0] || 'PI';
        setSelectedCurrency(defaultCurrency);
      } else {
        console.warn('⚠️ Invalid balance response:', response);
        setSelectedCurrency('PI');
      }
    } catch (error) {
      console.error('Failed to load balances:', error);
      toast.error('Failed to load available balances');
      setSelectedCurrency('PI');
    } finally {
      setIsLoadingBalance(false);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await withdrawalService.getBanks();
      if (response.success) {
        setBanks(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch banks:', error);
    }
  };

  const resolveAccountName = async () => {
    try {
      setIsResolvingAccount(true);
      const response = await withdrawalService.resolveAccount(formData.accountNumber, formData.bankCode);
      if (response.success) {
        setFormData(prev => ({ ...prev, accountName: response.data.account_name }));
        setErrors(prev => ({ ...prev, accountNumber: '' }));
      }
    } catch (error) {
      console.error('Failed to resolve account:', error);
      setFormData(prev => ({ ...prev, accountName: '' }));
      setErrors(prev => ({ ...prev, accountNumber: 'Could not verify account name' }));
    } finally {
      setIsResolvingAccount(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate amount
    const amountValidation = withdrawalService.validateWithdrawalAmount(formData.amount, selectedCurrency);
    if (!amountValidation.valid) {
      newErrors.amount = amountValidation.error;
    } else {
      const amount = parseFloat(formData.amount);
      if (amount > currentBalance) {
        newErrors.amount = `Amount exceeds available balance of ${withdrawalService.formatAmount(currentBalance, selectedCurrency)}`;
      }
    }

    if (isNGN) {
      if (!formData.bankCode) newErrors.bank = 'Please select a bank';
      if (!formData.accountNumber || formData.accountNumber.length !== 10) {
        newErrors.accountNumber = '10-digit account number is required';
      } else if (!formData.accountName) {
        newErrors.accountNumber = 'Account name must be verified';
      }
    } else {
      // Validate wallet address (Pi only)
      const walletValidation = withdrawalService.validateWalletAddress(formData.recipientWallet);
      if (!walletValidation.valid) {
        newErrors.recipientWallet = walletValidation.error;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleBankChange = (e) => {
    const selectedBank = banks.find(b => b.code === e.target.value);
    setFormData(prev => ({
      ...prev,
      bankCode: e.target.value,
      bankName: selectedBank?.name || '',
      accountName: '' // Reset account name on bank change
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const withdrawalData = {
        projectId: project.id,
        amount: parseFloat(formData.amount),
        currency: selectedCurrency,
        reason: formData.reason.trim() || undefined
      };

      if (isNGN) {
        withdrawalData.bankCode = formData.bankCode;
        withdrawalData.accountNumber = formData.accountNumber;
        withdrawalData.bankName = formData.bankName;
      } else {
        withdrawalData.recipientWallet = formData.recipientWallet.trim();
      }

      const response = await withdrawalService.requestWithdrawal(withdrawalData);

      if (response.success) {
        const { withdrawal } = response.data;

        toast.success(
          `Withdrawal request submitted for admin review. You'll be notified once it's processed.`
        );

        onSuccess(withdrawal);
      }
    } catch (error) {
      console.error('Withdrawal request failed:', error);
      toast.error(error.message || 'Failed to submit withdrawal request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Withdraw Funds</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-6">
          {/* Currency Tabs */}
          {availableCurrencies.length > 1 && (
            <div className="flex gap-2 mb-4">
              {availableCurrencies.map(curr => (
                <button
                  key={curr}
                  type="button"
                  onClick={() => {
                    setSelectedCurrency(curr);
                    // Reset form data when switching currencies
                    setFormData(prev => ({
                      ...prev,
                      amount: '',
                      recipientWallet: '',
                      bankCode: '',
                      bankName: '',
                      accountNumber: '',
                      accountName: ''
                    }));
                    setErrors({});
                  }}
                  className={`flex-1 px-3 py-2 md:px-4 md:py-3 rounded-lg font-medium text-sm transition-all ${selectedCurrency === curr
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <div className="flex flex-col items-center">
                    <span>{curr === 'PI' ? 'π Pi' : curr === 'NGN' ? '₦ Naira' : curr}</span>
                    <span className="text-xs opacity-80">
                      {withdrawalService.formatAmount(allBalances[curr]?.available || 0, curr)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Project Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">{project.title}</h3>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Total Raised ({selectedCurrency}):</span>
              <span className="font-medium">{withdrawalService.formatAmount(balanceBreakdown?.gross || 0, selectedCurrency)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-600 mt-1 relative group cursor-help">
              <span className="border-b border-dashed border-gray-400">Available to Withdraw:</span>
              <span className="font-medium text-teal-600">
                {isLoadingBalance ? (
                  <Loader2 className="w-4 h-4 animate-spin inline" />
                ) : (
                  withdrawalService.formatAmount(currentBalance, selectedCurrency)
                )}
              </span>

              {/* Balance Breakdown Tooltip */}
              {balanceBreakdown && (
                <div className="absolute left-0 top-full mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-10 hidden group-hover:block">
                  <h4 className="text-xs font-semibold text-gray-900 mb-2 border-b pb-1">Balance Breakdown ({selectedCurrency})</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Gross Raised:</span>
                      <span className="font-medium">{withdrawalService.formatAmount(balanceBreakdown.gross, selectedCurrency)}</span>
                    </div>
                    <div className="flex justify-between text-red-500">
                      <span>Fees (3% + Flutterwave):</span>
                      <span>-{withdrawalService.formatAmount(balanceBreakdown.fees, selectedCurrency)}</span>
                    </div>
                    <div className="flex justify-between text-gray-500">
                      <span>Already Withdrawn:</span>
                      <span>-{withdrawalService.formatAmount(balanceBreakdown.withdrawn, selectedCurrency)}</span>
                    </div>
                    {balanceBreakdown.pending > 0 && (
                      <div className="flex justify-between text-yellow-600">
                        <span>Pending Request:</span>
                        <span>-{withdrawalService.formatAmount(balanceBreakdown.pending, selectedCurrency)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t pt-1 mt-1 font-bold text-teal-600">
                      <span>Net Available:</span>
                      <span>{withdrawalService.formatAmount(balanceBreakdown.available, selectedCurrency)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Withdrawal Amount ({isNGN ? '₦' : 'π'})
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min={isNGN ? "100" : "10"}
                max={currentBalance}
                value={formData.amount}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                placeholder={`Enter amount in ${isNGN ? 'Naira' : 'Pi'}`}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 
                           ${errors.amount ? 'border-red-300' : 'border-gray-300'}`}
                disabled={isSubmitting || isLoadingBalance}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                <span className="text-gray-500 text-sm">{isNGN ? '₦' : 'π'}</span>
              </div>
            </div>
            {errors.amount && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.amount}
              </p>
            )}
            {formData.amount && !errors.amount && (
              <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p><strong>Manual Review:</strong> This withdrawal requires admin approval and may take 1-3 business days to process.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {isNGN ? (
            /* Bank Details (NGN) */
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Bank
                </label>
                <select
                  value={formData.bankCode}
                  onChange={handleBankChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 
                             ${errors.bank ? 'border-red-300' : 'border-gray-300'}`}
                  disabled={isSubmitting}
                >
                  <option value="">Select a bank...</option>
                  {banks.map(bank => (
                    <option key={bank.id} value={bank.code}>{bank.name}</option>
                  ))}
                </select>
                {errors.bank && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.bank}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Number
                </label>
                <div className="relative">
                  <input
                    type="text"
                    maxLength="10"
                    value={formData.accountNumber}
                    onChange={(e) => handleInputChange('accountNumber', e.target.value.replace(/\D/g, ''))}
                    placeholder="10-digit account number"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 
                               ${errors.accountNumber ? 'border-red-300' : 'border-gray-300'}`}
                    disabled={isSubmitting}
                  />
                  {isResolvingAccount && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
                    </div>
                  )}
                </div>
                {errors.accountNumber && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.accountNumber}
                  </p>
                )}
                {formData.accountName && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-green-700 bg-green-50 p-2 rounded-lg border border-green-100">
                    <CheckCircle className="w-4 h-4" />
                    <span><strong>Account Name:</strong> {formData.accountName}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Wallet Address (Pi) */
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pi Wallet Address
              </label>
              <input
                type="text"
                value={formData.recipientWallet}
                onChange={(e) => handleInputChange('recipientWallet', e.target.value)}
                placeholder="G... (56 characters)"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 
                           ${errors.recipientWallet ? 'border-red-300' : 'border-gray-300'}`}
                disabled={isSubmitting}
              />
              {errors.recipientWallet && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.recipientWallet}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Enter your Pi wallet address where you want to receive the funds
              </p>
            </div>
          )
          }

          {/* Reason Input (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Withdrawal <span className="text-gray-400">(Optional)</span>
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              placeholder="e.g., Project completion, milestone reached, operational expenses..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              disabled={isSubmitting}
            />
          </div>

          {/* Important Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">Important:</p>
                <ul className="space-y-1 text-xs">
                  <li>• Minimum withdrawal: {isNGN ? '₦100' : '10 π'}</li>
                  <li>• Up to 5 withdrawals per project per 24 hours</li>
                  <li>• {isNGN ? 'Bank transfers' : 'Blockchain transactions'} cannot be reversed</li>
                  <li>• Please ensure all details are accurate</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Fee Display (NGN Only) */}
          {isNGN && fees.totalFee > 0 && (
            <div className="p-3 bg-gray-50 rounded-lg space-y-1 text-sm border border-gray-100">
              <div className="flex justify-between text-gray-600">
                <span>Transfer Fee (Flutterwave)</span>
                <span>₦{fees.baseFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>VAT (7.5%)</span>
                <span>₦{fees.vat.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-medium text-gray-900 pt-1 border-t border-gray-200">
                <span>Total to be debited</span>
                <span>₦{(parseFloat(formData.amount) + fees.totalFee || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          )}

          {/* Reason Input (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Withdrawal <span className="text-gray-400">(Optional)</span>
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              placeholder="e.g., Project completion, milestone reached, operational expenses..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              disabled={isSubmitting}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 
                         transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isLoadingBalance || !currentBalance || (isNGN && !formData.accountName)}
              className="flex-1 px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 
                         text-white rounded-lg transition-colors focus:outline-none focus:ring-2 
                         focus:ring-teal-500 focus:ring-offset-2 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Submit Withdrawal
                </>
              )}
            </button>
          </div>
        </form >
      </div >
    </div >
  );
};

export default WithdrawalModal;
